import React, { useRef, useState } from 'react';
import { UploadCloud, ArrowDown } from 'lucide-react';

interface MediaDropzoneProps {
  embedded?: boolean;
  accept?: string;
  title?: string;
  description?: React.ReactNode;
  multiple?: boolean;
  onFileSelected?: (file: File) => void;
  onFilesSelected?: (files: File[]) => void;
}

const MediaDropzone: React.FC<MediaDropzoneProps> = ({
  embedded = false,
  accept = 'audio/*,video/*,.ts,.mts,.m2ts,.mpeg,.mpg,.vob,.avi,.wmv,.flv,.3gp',
  title = 'Click to upload or drag media',
  description = (
    <>
      Video files are converted to audio in your browser before upload.<br />
      Audio: aac, flac, m4a, mp3, ogg, opus, wav, weba, webm<br />
      Video: avi, mkv, mov, mp4, ts, webm, wmv<br />
      Max file size 5GB.
    </>
  ),
  multiple = false,
  onFileSelected,
  onFilesSelected,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      if (multiple && onFilesSelected) {
        onFilesSelected(files);
      } else if (onFileSelected) {
        onFileSelected(files[0]);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.currentTarget.contains(e.relatedTarget as Node)) {
      return;
    }
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      if (multiple && onFilesSelected) {
        onFilesSelected(files);
      } else if (onFileSelected) {
        onFileSelected(files[0]);
      }
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className={`
        relative group rounded-[20px] border-[1.5px] border-dashed flex flex-col items-center justify-center text-center transition-all duration-300 cursor-pointer overflow-hidden
        ${embedded ? 'py-14' : 'py-12'}
        ${isDragging
          ? 'border-accent bg-accent/[0.06]'
          : 'border-black/[0.12] bg-card hover:border-accent/50 hover:bg-accent/[0.03] dark:border-white/[0.14]'
        }
      `}
      onClick={() => fileInputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          fileInputRef.current?.click();
        }
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept={accept}
        multiple={multiple}
        onChange={handleFileChange}
      />

      <div className={`absolute inset-0 z-10 bg-accent/[0.08] backdrop-blur-[2px] flex flex-col items-center justify-center transition-opacity duration-200 pointer-events-none ${isDragging ? 'opacity-100' : 'opacity-0'}`}>
        <div className="grid h-14 w-14 place-items-center rounded-full bg-accent text-white shadow-lg mb-3 animate-bounce">
          <ArrowDown className="h-6 w-6" />
        </div>
        <h3 className="text-[17px] font-semibold text-accent">
          {multiple ? 'Drop files here' : 'Drop file here'}
        </h3>
      </div>

      <div className="grid h-14 w-14 place-items-center rounded-[16px] bg-gradient-to-b from-[#2b8cff] to-[#0062e0] text-white shadow-[0_8px_20px_-6px_rgba(0,98,224,0.55)] mb-5 transition-transform duration-300 group-hover:-translate-y-0.5 relative z-0">
        <UploadCloud className="h-7 w-7" />
      </div>
      <h4 className="text-[17px] font-semibold tracking-[-0.01em] text-ink mb-1.5 relative z-0">{title}</h4>
      <p className="text-ink-2 max-w-sm mx-auto px-4 text-[13px] leading-relaxed relative z-0">
        {description}
      </p>
    </div>
  );
};

export default MediaDropzone;
